<template>
  <div>
    <provisioning-status @refresh="load" />
    <v-card class="main-card">
      <v-card-title class="screen-title">
        <span>IPsecGW一覧</span>
        <manual-button path="/ipsec-gw/" />
      </v-card-title>
      <v-card-text class="pa-2" v-if="isLoaded">
        <v-card class="mb-4">
          <v-card-title class="d-flex align-center">
            <h4>V番号全体のIPsec回線利用状況</h4>
            <span class="text-caption ml-2"
              >※IPsecGW回線番号ごとの上限値ではありません。</span
            >
          </v-card-title>
          <v-card-text class="pt-0">
            <v-row align="center" no-gutters>
              <v-col>
                <div class="d-flex align-center ga-6 mb-2">
                  <span class="text-body-1">
                    利用中:
                    <span>{{ contractCurrentUsage }}回線</span>
                  </span>
                  <span class="text-body-1">
                    上限値:
                    <span>{{ contractUpperLimit }}回線</span>
                  </span>
                </div>
              </v-col>
              <v-col cols="auto">
                <v-btn
                  v-permission.admin
                  color="primary"
                  @click="modifyContractLimit"
                >
                  上限値設定
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
        <v-row no-gutters>
          <v-col>
            <app-search
              :search="gatewayList.search"
              v-model="gatewayList.filter"
            />
          </v-col>
          <div class="d-flex flex-row ga-1 flex-wrap ml-2">
            <v-btn :href="$api.ipSecGw.getCsvURL()" color="primary">
              一覧ダウンロード
            </v-btn>
            <v-btn color="primary" @click="load"> 一覧更新 </v-btn>
          </div>
        </v-row>
        <app-table
          v-model:table-sort-by="gatewayList.sortBy"
          primary-key="enumber"
          :fields="gatewayList.fields"
          :items="items"
          :filter-option="gatewayList.filter_option"
          :filter="gatewayList.filter"
          :hover="true"
          sticky-header="50vh"
          @click:row="showIPsecGWReference"
        >
          <template #cell(suffix)="{ value }">
            {{ $filter.emptyTo(value, "-") }}
          </template>
          <template #cell(lineSize)="{ item }">
            {{ item.numberOfIpsec }}/{{ item.upperLimitIpsec }}回線
          </template>
        </app-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { IpSecGWEntity, IpSecGWGetEntity } from "@/apis/IpSecGwApi";
import type { ContractLineGetEntity } from "@/apis/ContractLineApi";
import type { UpperLimitOptionGetEntity } from "@/apis/UpperLimitApi";
import IpSecGWReference from "@/modals/ipSecGw/IpSecGWReference.vue";
import type {
  AppTableHeader,
  FilterOption,
  SortItem,
} from "@/components/AppTable.vue";
import type { SearchOption } from "@/components/AppSearch.vue";
import LimitModify from "@/modals/common/LimitModify.vue";

/** 表示可能なIPsecGW（いくつかの項目が存在すること） */
export type VisibleIpSecGW = Omit<
  IpSecGWEntity,
  "ipsecGatewayAddress" | "manageAddress1" | "manageAddress2" | "manageAddress3"
> & {
  /** IPsecGWアドレス*/
  ipsecGatewayAddress: string;
  /** 管理用アドレス① */
  manageAddress1: string;
  /** 管理用アドレス② */
  manageAddress2: string;
  /** 管理用アドレス③ */
  manageAddress3: string;
};

export default defineComponent({
  name: "IpSecGWList",
  data() {
    return {
      gatewayList: {
        sortBy: { key: "enumber", order: "desc" } as SortItem,
        fields: [
          {
            key: "enumber",
            title: "IPsecGW回線番号",
            sortable: true,
            width: "200px",
          },
          {
            key: "suffix",
            title: "サフィックス",
            width: "200px",
          },
          {
            key: "lineSize",
            title: "IPsec回線数/IPsec回線接続上限数",
            width: "250px",
            align: "end",
          },
        ] as AppTableHeader<VisibleIpSecGW>[],
        items: [] as IpSecGWEntity[],
        search: {
          enumber: { type: "text", label: "IPsecGW回線番号" },
          suffix: { type: "text", label: "サフィックス" },
        } as SearchOption,
        filter: {} as Record<string, unknown>,
        filter_option: {
          enumber: "like",
          suffix: "like",
        } as FilterOption,
      },
      ipSecGw: {} as IpSecGWGetEntity,
      contractLine: {} as ContractLineGetEntity,
      upperLimit: {} as UpperLimitOptionGetEntity,
      isLoaded: false,
    };
  },
  computed: {
    /**
     * 表示可能なIPsecGW.
     * サービス開廃日が未来日じゃない & WANアドレス/IPsecGWアドレス/管理用アドレス①,②,③が登録されている
     */
    items(): VisibleIpSecGW[] {
      const today = this.$moment().startOf("day");
      const contractMap = this.contractLine.contractList.toMap(
        (e) => e.enumber
      );
      return this.ipSecGw.ipsecGatewayList.filter((e) => {
        const startEndDate = contractMap[e.enumber]?.startEndDate;
        return (
          startEndDate &&
          today.isSameOrAfter(this.$moment(startEndDate)) &&
          e.wanAddress != null &&
          e.ipsecGatewayAddress != null &&
          e.manageAddress1 != null &&
          e.manageAddress2 != null &&
          e.manageAddress3 != null
        );
      }) as VisibleIpSecGW[];
    },
    /** 契約全体の現在使用回線数 */
    contractCurrentUsage(): number {
      return this.upperLimit.ipsec?.numberOfIpsec || 0;
    },
    /** 契約全体の上限回線数 */
    contractUpperLimit(): number {
      return this.upperLimit.ipsec?.upperLimitIpsec || 0;
    },
  },
  async mounted() {
    await this.load();
    this.isLoaded = true;
  },
  methods: {
    async load() {
      this.contractLine = await this.$api.contractLine.getAll();
      this.ipSecGw = await this.$api.ipSecGw.getAll();
      this.upperLimit = await this.$api.upperLimit.getUpperLimitOption();
    },

    /** 上限数設定 */
    async modifyContractLimit() {
      await this.$modal.show(LimitModify, {
        settingType: "ipSecLine",
      });
      await this.load();
    },

    /** IPsecGW情報モーダル表示 */
    async showIPsecGWReference(item: IpSecGWEntity) {
      await this.$modal.show(IpSecGWReference, {
        ipsecGW: item,
        keyId: this.ipSecGw.ipsecGatewayKeyId,
      });
      await this.load();
    },
  },
});
</script>
